import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { SelfCertificationApiService } from './self-certification-api.service';
import { ValidateTokenResponse } from './types/self-certification-api.models';
import { SelfCertificationPageState } from './types/page-state.type';

const MAX_SDK_ERROR_LENGTH = 500;

/**
 * Reduces an SDK failure to a short message safe to send onwards.
 *
 * Only the message is taken, never the error object: SDK errors wrap the request that failed,
 * and its headers carry the TaxBit bearer token. This string leaves the browser.
 */
function describeSdkError(err: unknown): string {
	const raw =
		err instanceof Error
			? err.message
			: typeof err === 'string'
			? err
			: (err as { message?: unknown } | null)?.message;

	const message = typeof raw === 'string' ? raw.trim() : '';

	return message.slice(0, MAX_SDK_ERROR_LENGTH) || 'Unknown SDK error';
}

@Component({
	selector: 'bcb-self-certification-page',
	templateUrl: './self-certification-page.component.html',
})
export class SelfCertificationPageComponent implements OnInit {
	state: SelfCertificationPageState = 'loading';
	bearerToken = '';
	entityName = '';
	errorReason = '';
	private accessToken = '';
	private sdkErrorReported = false;

	constructor(
		private readonly route: ActivatedRoute,
		private readonly api: SelfCertificationApiService
	) {}

	ngOnInit(): void {
		this.accessToken = this.route.snapshot.paramMap.get('token') ?? '';
		if (!this.accessToken) {
			this.state = 'error';
			this.errorReason = 'INVALID';

			return;
		}

		this.initialize();
	}

	async onFormSuccess(): Promise<void> {
		this.state = 'submitting';

		try {
			await this.api.notifySubmitted(this.accessToken);
		} catch (err) {
			console.error(
				'Failed to notify submission, but form was submitted to TaxBit',
				err
			);
		}
		this.state = 'confirmed';
	}

	onFormError(err: unknown): void {
		console.error('TaxBit SDK error', err);

		this.state = 'error';
		this.errorReason = 'SDK_ERROR';

		this.reportSdkError(err);
	}

	/**
	 * A single SDK failure can raise several errors, and nobody needs the same alert repeatedly.
	 * Fire-and-forget: the page is already showing its error state and must not depend on this.
	 */
	private reportSdkError(err: unknown): void {
		if (this.sdkErrorReported) {
			return;
		}
		this.sdkErrorReported = true;

		this.api
			.reportSdkError(this.accessToken, describeSdkError(err))
			.catch((reportErr) =>
				console.error('Failed to report TaxBit SDK error', reportErr)
			);
	}

	private async initialize(): Promise<void> {
		try {
			const validation: ValidateTokenResponse =
				await this.api.validateToken(this.accessToken);

			if (!validation.valid) {
				this.applyFailure(validation.reason);

				return;
			}

			this.entityName = validation.entityName ?? '';

			const tokenResponse = await this.api.getBearerToken(
				this.accessToken
			);

			this.bearerToken = tokenResponse.bearerToken;
			this.state = 'form';
		} catch (err: unknown) {
			console.error('Self-certification initialization failed', err);

			this.applyFailure((err as { reason?: string })?.reason);
		}
	}

	private applyFailure(reason?: string): void {
		if (reason === 'PAUSED') {
			this.state = 'paused';

			return;
		}

		this.state = 'error';
		this.errorReason = reason ?? 'INVALID';
	}
}
